import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { swalSuccess, swalError } from '../utils/swal';

function postLoginPath(location) {
  const from = location.state?.from;
  const path =
    typeof from === 'string'
      ? from
      : from?.pathname && from.pathname !== '/' && from.pathname !== '/login'
        ? `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`
        : null;
  return path && path.startsWith('/') ? path : '/sale';
}

function IconEye({ open }) {
  if (open) {
    return (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
        <path
          d="M3.5 10s2.5-4 6.5-4 6.5 4 6.5 4-2.5 4-6.5 4-6.5-4-6.5-4Z"
          stroke="currentColor"
          strokeWidth="1.35"
          strokeLinejoin="round"
        />
        <circle cx="10" cy="10" r="2" stroke="currentColor" strokeWidth="1.35" />
        <path
          d="M4 4l12 12"
          stroke="currentColor"
          strokeWidth="1.35"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
      <path
        d="M3.5 10s2.5-4 6.5-4 6.5 4 6.5 4-2.5 4-6.5 4-6.5-4-6.5-4Z"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinejoin="round"
      />
      <circle cx="10" cy="10" r="2" stroke="currentColor" strokeWidth="1.35" />
    </svg>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const r = await login(email, password);
    setSubmitting(false);
    if (!r.ok) {
      const msg = r.error ?? 'Invalid credentials.';
      setError(msg);
      swalError('Sign in failed', msg);
      return;
    }
    await swalSuccess('Welcome', 'You are signed in.');
    navigate(postLoginPath(location), { replace: true });
  }

  return (
    <div className="login-page">
      <div className="login-page-bg" aria-hidden />
      <div className="login-shell">
        <div className="login-card card login-card-elevated">
          <div className="login-card-accent" aria-hidden />
          <div className="login-card-body">
            <div className="login-brand">
              <img
                className="login-logo"
                src={`${process.env.PUBLIC_URL}/icon-512.png`}
                alt=""
                width={80}
                height={80}
                decoding="async"
              />
              <div className="login-brand-text">
                <h1 className="login-title">POS system</h1>
                <p className="login-tagline">Point of sale for your store</p>
              </div>
            </div>

            <form className="login-form" onSubmit={handleLogin} noValidate>
              <label className="field">
                <span className="field-label">Email</span>
                <input
                  className="input login-input"
                  type="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError(null);
                  }}
                  placeholder="you@example.com"
                  autoComplete="username"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck="false"
                  autoFocus
                  required
                  disabled={submitting}
                  aria-invalid={error ? 'true' : undefined}
                  aria-describedby={error ? 'login-error-msg' : undefined}
                />
              </label>
              <label className="field">
                <span className="field-label">Password</span>
                <div className="login-password-wrap">
                  <input
                    className="input login-input login-password-input"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    required
                    disabled={submitting}
                    aria-invalid={error ? 'true' : undefined}
                    aria-describedby={error ? 'login-error-msg' : undefined}
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    disabled={submitting}
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    <IconEye open={showPassword} />
                  </button>
                </div>
              </label>
              <div
                id="login-error-msg"
                className="login-error-region"
                role="alert"
                aria-live="polite"
              >
                {error ? <p className="login-error">{error}</p> : null}
              </div>
              <button
                type="submit"
                className="btn btn-primary btn-block btn-lg login-submit"
                disabled={submitting}
              >
                {submitting ? (
                  <span className="login-submit-inner">
                    <span className="login-spinner" aria-hidden />
                    Signing in…
                  </span>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>

            <p className="login-footer">
              Secure session · Your data stays with your business
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
